export * from './next-shim-base'
import React from 'react'
import { useTranslation, Trans } from 'react-i18next'
import type { TFunction as I18nTFunction } from 'i18next'
import i18n from '@/lib/i18n/i18n'
import { Image } from './next-shim-base'

// useLocale Shim (for next-intl)
export const useLocale = () => {
  const { i18n: i18nInstance } = useTranslation()
  return i18nInstance.language
}

type RichValues = Record<string, React.ReactNode | ((chunks: React.ReactNode) => React.ReactNode) | string | number>

interface TranslatorFunction {
  (key: string, values?: Record<string, unknown>): string
  rich: (key: string, values?: RichValues) => React.ReactNode
}

const createRichTranslator = (namespace: string | undefined, t: I18nTFunction): TranslatorFunction => {
  const transFunc = ((key: string, values?: Record<string, unknown>) => {
    const fullKey = namespace ? `${namespace}.${key}` : key
    return t(fullKey, { ...values, interpolation: { prefix: '{', suffix: '}' } })
  }) as TranslatorFunction

  transFunc.rich = (key: string, values?: RichValues) => {
    const fullKey = namespace ? `${namespace}.${key}` : key

    // Separate components (functions/elements) from interpolation values
    const components: Record<string, React.ReactElement> = {}
    const interpolationValues: Record<string, unknown> = {}

    if (values) {
      Object.keys(values).forEach((k) => {
        const val = values[k]
        if (typeof val === 'function' || React.isValidElement(val)) {
          // For react-i18next Trans, functions are treated as components
          // next-intl passes 'chunks' to functions, react-i18next passes 'children'
          if (typeof val === 'function') {
            components[k] = React.createElement(({ children }: { children?: React.ReactNode }) => <>{val(children)}</>)
          } else {
            components[k] = val
          }
        } else {
          interpolationValues[k] = val
        }
      })
    }

    return <Trans i18nKey={fullKey} t={t} values={interpolationValues} components={components} tOptions={{ interpolation: { prefix: '{', suffix: '}' } }} />
  }

  return transFunc
}

// useTranslations Shim (for next-intl)
export const useTranslations = (namespace?: string): TranslatorFunction => {
  const { t } = useTranslation()
  return createRichTranslator(namespace, t)
}

// notFound Shim
export class NotFoundError extends Error {
  constructor() {
    super('Not Found')
    this.name = 'NotFoundError'
  }
}

export const notFound = () => {
  throw new NotFoundError()
}

// getTranslations Shim (Client-side)
export const getTranslations = (namespace?: string): TranslatorFunction => {
  // Note: in client-side shim, we use the same hook-based implementation
  // as we don't have a real server-side context in this Vite setup.
  const { t } = useTranslation()
  return createRichTranslator(namespace, t)
}
